import { Directive, ElementRef, Input, HostListener, Output, EventEmitter, Renderer2, NgZone, OnInit, OnDestroy } from '@angular/core';
import { modKeyDown } from '../shared/utils/mod-key-down.util';

export interface BbDragEvent {
	type: 'dragstarted' | 'dragended' | 'dragmoved';
	left: number;
	top: number;
	offset: { // distance from start
		x: number;
		y: number;
	};
	source: {
		event: MouseEvent;
		element: HTMLElement;
	};
	snappedX: boolean;
	snappedY: boolean;
	snapValues?: any;
	relativeSnapPointOffsetLeft: number;
	relativeSnapPointOffsetTop: number;
}

export interface BbSnapCoords {
	points: any; // x652: number, y204: number, etc.
	pointDistance: number;
	lines: {
		x: any; // x652: number, x804: number, etc.
		y: any; // y0: number, x804: number, etc.
	};
	lineDistance: number;
}

@Directive({
	selector: '[bbDrag]'
})

export class BbDragDirective implements OnInit, OnDestroy {
	@Output() bbDragStarted: EventEmitter<BbDragEvent> = new EventEmitter<BbDragEvent>();
	@Output() bbDragMoved: EventEmitter<BbDragEvent> = new EventEmitter<BbDragEvent>();
	@Output() bbDragEnded: EventEmitter<BbDragEvent> = new EventEmitter<BbDragEvent>();

	@Input() draggingClass = 'bb-dragging';
	@Input('bbDragAxis') axis: 'x' | 'y';
	@Input('bbDragConstraint') constraint: HTMLElement;
	@Input('bbDragContainer') container: HTMLElement;
	@Input('bbDragContainerQuery') containerQuery: string;
	@Input('bbDragDisabled') disabled = false;
	@Input('bbDragUseSnapping') useSnapping = false;
	@Input('bbDragSnapCoords') snapCoords: any;
	@Input('bbDragRelativeSnapPoints') relativeSnapPoints: any = [{
		x: 0,
		y: 0
	}]; // default: snap only to topleft. x and y can range from 0 to 1 (1,1 being the bottom-right)
	@Input('bbDragPointSnapDistance') pointSnapDistance = 10;
	@Input('bbDragLineSnapDistance') lineSnapDistance = 8;
	@Input('bbDragShowSnapGuides') showSnapGuides = false;

	dragEl: HTMLElement;

	elStartProps: DOMRect | ClientRect;
	containerStartProps: DOMRect | ClientRect;
	constraintStartProps: DOMRect | ClientRect;
	mouseOffset: any;
	mouseMoveListener: any;
	mouseUpListener: any;
	docMouseUpListener: any;
	dragStartListener: any;
	dragging = false;
	mouseMoved = false;
	mouseDownDragEvent: BbDragEvent = null;
	snapLineX: HTMLElement;
	snapLineY: HTMLElement;

	preDragUserSelect: any;

	constructor(
		private el: ElementRef,
		private ngZone: NgZone,
		private renderer: Renderer2
	) {	}

	@HostListener('mousedown', ['$event'])
	onMouseDown(ev: MouseEvent) {
		this.mouseMoved = false;
		this.ngZone.runOutsideAngular(() => {
			if (ev.button === 0) {
				this.setSnapLinesSize();
				this.stopListeners();
				if (!this.disabled) {
					this.dragging = true;
					this.elStartProps = this.el.nativeElement.getBoundingClientRect();
					this.containerStartProps = this.container.getBoundingClientRect();
					this.constraintStartProps = this.constraint.getBoundingClientRect();
					this.mouseOffset = {
						left: ev.pageX - this.elStartProps.left,
						top: ev.pageY - this.elStartProps.top,
					};
					this.mouseMoveListener = this.renderer.listen( document, 'mousemove', (mev: MouseEvent) => {
						this.onMouseMove(mev);
					});
					this.mouseUpListener = this.renderer.listen( this.dragEl, 'mouseup', (mev: MouseEvent) => {
						this.onMouseUp(mev);
					});
					this.docMouseUpListener = this.renderer.listen( document, 'mouseup', (mev: MouseEvent) => {
						this.onDocumentMouseUp(mev);
					});

					this.mouseDownDragEvent = this.createBbDragEvent(ev);
					// console.warn('MOUSEDOWN',
					// 'this.elStartProps', this.elStartProps,
					// 'this.containerStartProps', this.containerStartProps,
					// 'this.mouseOffset', this.mouseOffset,
					// 'pos', pos
					// )
				}
			}
		});
	}

	ngOnInit() {
		this.dragEl = this.el.nativeElement;
		if (this.container === null && this.containerQuery) {
			this.container = this.dragEl.closest(this.containerQuery) as HTMLElement;
		}
		this.constraint = this.constraint || this.container;

		this.ngZone.runOutsideAngular(() => {
			this.dragStartListener = this.renderer.listen( this.dragEl, 'dragstart', (dev: DragEvent) => {
				dev.preventDefault();
				dev.stopPropagation();
			});
			this.initializeSnapGuides();
		});
	}

	ngOnDestroy() {
		this.stopListeners();
		if (this.dragStartListener) {
			this.dragStartListener();
		}
	}

	stopListeners() {
		// removes listeners
		if (this.mouseMoveListener) {
			this.mouseMoveListener();
		}
		if (this.mouseUpListener) {
			this.mouseUpListener();
		}
		if (this.docMouseUpListener) {
			this.docMouseUpListener();
		}
	}

	stopDrag(ev: MouseEvent) {
		this.stopListeners();
		if (this.dragging && this.mouseMoved) {
			this.mouseMoved = false;
			this.dragging = false;
			this.dragEl.classList.remove(this.draggingClass);
			if (this.preDragUserSelect) {
				document.body.style.userSelect = this.preDragUserSelect;
				this.preDragUserSelect = null;
			}
			const bbDragEvent = this.createBbDragEvent(ev);
			this.hideSnapLines();
			this.bbDragEnded.emit(bbDragEvent);
		}
	}

	private initializeSnapGuides() {
		if (this.showSnapGuides && this.constraint) {
			this.snapLineX = this.constraint.querySelector('.bb-drag-snap-line-x');
			this.snapLineY = this.constraint.querySelector('.bb-drag-snap-line-y');
			if (this.snapLineX && this.snapLineY) {
				return;
			} else {
				this.snapLineX = document.createElement('div');
				this.snapLineY = document.createElement('div');
			}
			const initialStyles = {
				position: 'absolute',
				pointerEvents: 'none',
				display: 'none',
				left: 0,
				top: 0,
				zIndex: 10,
				backgroundColor: 'rgba(255, 0, 0, 0.8)',
			};
			for (const prop of Object.keys(initialStyles)) {
				this.snapLineX.style[prop.toString()] = initialStyles[prop.toString()];
				this.snapLineY.style[prop.toString()] = initialStyles[prop.toString()];
			}
			this.snapLineX.style.height = '1px';
			this.snapLineY.style.width = '1px';
			this.setSnapLinesSize();
			this.snapLineX.classList.add('bb-drag-snap-line-x');
			this.snapLineY.classList.add('bb-drag-snap-line-y');
			this.constraint.appendChild(this.snapLineX);
			this.constraint.appendChild(this.snapLineY);
		}
	}

	private setSnapLinesSize() {
		if (this.snapLineX && this.snapLineY) {
			this.snapLineX.style.width = this.constraint.offsetWidth + 'px';
			this.snapLineY.style.height = this.constraint.offsetHeight + 'px';
		}
	}

	private updateSnapLines(bbDragEvent: BbDragEvent) {
		if (this.snapLineY) {
			if (bbDragEvent.snappedX) {
				this.snapLineY.style.left = bbDragEvent.left + bbDragEvent.relativeSnapPointOffsetLeft + 'px';
				this.snapLineY.style.display = 'block';
			} else {
				this.snapLineY.style.display = 'none';
			}
		}

		if (this.snapLineX) {
			if (bbDragEvent.snappedY) {
				this.snapLineX.style.top = bbDragEvent.top + bbDragEvent.relativeSnapPointOffsetTop + 'px';
				this.snapLineX.style.display = 'block';
			} else {
				this.snapLineX.style.display = 'none';
			}
		}
	}

	private hideSnapLines() {
		if (this.snapLineX && this.snapLineY) {
			this.snapLineY.style.display = 'none';
			this.snapLineX.style.display = 'none';
		}
	}

	private onMouseMove(ev: MouseEvent) {
		if (!this.mouseMoved) {
			this.mouseMoved = true;
			this.preDragUserSelect = document.body.style.userSelect;
			document.body.style.userSelect = 'none';
			this.dragEl.style.userSelect = 'none';

			this.dragEl.classList.add(this.draggingClass);
			this.bbDragStarted.emit(this.mouseDownDragEvent);
		}
		this.ngZone.runOutsideAngular(() => {
			const bbDragEvent = this.createBbDragEvent(ev);
			this.updateSnapLines(bbDragEvent);

			if (this.axis !== 'y') {
				this.renderer.setStyle(this.el.nativeElement, 'left', bbDragEvent.left + 'px');
			}
			if (this.axis !== 'x') {
				this.renderer.setStyle(this.el.nativeElement, 'top', bbDragEvent.top + 'px');
			}
			this.bbDragMoved.emit(bbDragEvent);
		});
	}

	private onMouseUp(ev: MouseEvent) {
		this.stopDrag(ev);
	}

	private onDocumentMouseUp(ev: MouseEvent) {
		this.stopDrag(ev);
	}

	private createBbDragEvent(ev: MouseEvent): BbDragEvent {
		const elRect = this.el.nativeElement.getBoundingClientRect();
		const modKey = modKeyDown(ev);
		const useSnapping = modKey ? !this.useSnapping : this.useSnapping;

		const de: BbDragEvent = {
			type: ev.type === 'mousedown' ? 'dragstarted' : (ev.type === 'mouseup' ? 'dragended' : 'dragmoved'),
			left: ev.pageX - this.mouseOffset.left,
			top: ev.pageY - this.mouseOffset.top,
			offset: {
				x: elRect.left - this.elStartProps.left,
				y: elRect.top - this.elStartProps.top
			},
			source: {
				event: ev,
				element: this.el.nativeElement
			},
			snappedX: false,
			snappedY: false,
			snapValues: {},
			relativeSnapPointOffsetLeft: 0,
			relativeSnapPointOffsetTop: 0,
		};
		// stay within constraint
		if (de.left < this.constraintStartProps.left) {
			de.left = this.constraintStartProps.left;
		}
		if (de.top < this.constraintStartProps.top) {
			de.top = this.constraintStartProps.top;
		}
		if (de.left + this.elStartProps.width > this.constraintStartProps.left + this.constraintStartProps.width) {
			de.left = this.constraintStartProps.left + this.constraintStartProps.width - this.elStartProps.width;
		}
		if (de.top + this.elStartProps.height > this.constraintStartProps.top + this.constraintStartProps.height) {
			de.top = this.constraintStartProps.top + this.constraintStartProps.height - this.elStartProps.height;
		}

		de.left = de.left - this.container.getBoundingClientRect().left;
		de.top = de.top - this.container.getBoundingClientRect().top;

		// snapping
		if (useSnapping) {
			let leftSnap = de.left;
			let topSnap = de.top;
			if (this.snapCoords && this.snapCoords.length) {
				for (let i = 0; i < this.snapCoords.length; i++) {
					const snapCoord = this.snapCoords[i];
					const snapRange = snapCoord.x !== undefined && snapCoord.y !== undefined ? this.pointSnapDistance : this.lineSnapDistance;
					if (snapCoord.x !== undefined || snapCoord.y !== undefined) {
						let minSnapDistanceX = Infinity;
						let minSnapDistanceY = Infinity;
						if (this.relativeSnapPoints && this.relativeSnapPoints.length) {
							for (let j = 0; j < this.relativeSnapPoints.length; j++) {
								const relativeSnapPoint = this.relativeSnapPoints[j];
								if (relativeSnapPoint.x !== undefined && snapCoord.x !== undefined) {
									const xExtra = (this.elStartProps.width * relativeSnapPoint.x);
									const xCoord = xExtra + de.left;
									const distanceX = Math.abs(snapCoord.x - xCoord);
									if (distanceX <= snapRange && distanceX < minSnapDistanceX) {
										minSnapDistanceX = distanceX;
										leftSnap = snapCoord.x - xExtra;
										de.snapValues['x' + String(relativeSnapPoint.x)] = snapCoord.xValue;
										de.relativeSnapPointOffsetLeft = xExtra;
									}
								}
								if (relativeSnapPoint.y !== undefined && snapCoord.y !== undefined) {
									const yExtra = (this.elStartProps.height * relativeSnapPoint.y);
									const yCoord = yExtra + de.top;
									const distanceY = Math.abs(snapCoord.y - yCoord);
									if (distanceY <= snapRange && distanceY < minSnapDistanceY) {
										minSnapDistanceY = distanceY;
										topSnap = snapCoord.y - yExtra;
										de.snapValues['y' + String(relativeSnapPoint.y)] = snapCoord.yValue;
										de.relativeSnapPointOffsetTop = yExtra;
									}
								}
							}
						}
					}
				}
			}

			if (de.left !== leftSnap) {
				de.left = leftSnap;
				de.snappedX = true;
			}

			if (de.top !== topSnap) {
				de.top = topSnap;
				de.snappedY = true;
			}
		}

		return de;
	}
}
